import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useLanguage } from '@/context/LanguageContext';
import { apiHelpers } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { 
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { 
  Plus, 
  Search,
  MapPin,
  Calendar,
  Clock,
  User,
  ChevronRight,
  Eye,
  FileText,
  ClipboardCheck
} from 'lucide-react';
import { format, parseISO, isToday, isTomorrow, isPast } from 'date-fns';

const SiteVisitsPage = () => {
  const { organization, user } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const [visits, setVisits] = useState([]);
  const [clients, setClients] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [showNewModal, setShowNewModal] = useState(false);
  const [newVisitData, setNewVisitData] = useState({
    client_id: '',
    job_address: '',
    scheduled_date: format(new Date(), 'yyyy-MM-dd'),
    scheduled_time: '09:00',
    assigned_estimator_id: '',
    service_type: '',
    project_notes: ''
  });

  // Check for action=new in URL and pre-select client if provided
  useEffect(() => {
    const action = searchParams.get('action');
    if (action === 'new') {
      setShowNewModal(true);
      // Pre-select client if client_id is provided
      const clientId = searchParams.get('client_id');
      if (clientId) {
        // Find the client to get their address and other details
        const client = clients.find(c => c.id === clientId);
        setNewVisitData(prev => ({
          ...prev,
          client_id: clientId,
          job_address: client?.address || prev.job_address
        }));
      }
      // Clear URL params immediately after reading to prevent reopening on refresh
      const newParams = new URLSearchParams(searchParams);
      newParams.delete('action');
      newParams.delete('client_id');
      window.history.replaceState({}, '', `${window.location.pathname}${newParams.toString() ? '?' + newParams.toString() : ''}`);
    }
  }, [searchParams, clients]);

  const loadData = useCallback(async () => {
    if (!organization?.id) return;
    
    try {
      const [visitsRes, clientsRes, employeesRes] = await Promise.all([
        apiHelpers.getSiteVisits(organization.id),
        apiHelpers.getClients(organization.id),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/organizations/${organization.id}/employees`).then(r => r.json())
      ]);
      
      setVisits(visitsRes.data);
      setClients(clientsRes.data);
      setEmployees(employeesRes || []);
    } catch (error) {
      console.error('Failed to load site visits:', error);
      toast.error('Failed to load site visits');
    } finally {
      setIsLoading(false);
    }
  }, [organization?.id]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleCreateVisit = async () => {
    if (!newVisitData.client_id) {
      toast.error('Please select a customer');
      return;
    }

    try {
      const response = await apiHelpers.createSiteVisit(
        organization.id,
        newVisitData,
        user.id,
        user.name
      );
      
      // Close modal first
      setShowNewModal(false);
      
      // Reset form data
      setNewVisitData({
        client_id: '',
        job_address: '',
        scheduled_date: format(new Date(), 'yyyy-MM-dd'),
        scheduled_time: '09:00',
        assigned_estimator_id: '',
        service_type: '',
        project_notes: ''
      });
      
      toast.success('Site visit scheduled!');
      
      // Navigate to the newly created site visit
      if (response?.data?.id) {
        navigate(`/site-visits/${response.data.id}`);
      } else {
        loadData();
      }
    } catch (error) {
      console.error('Failed to create site visit:', error);
      // Extract proper error message
      let errorMsg = 'Failed to schedule site visit';
      if (error.response?.data?.detail) {
        if (typeof error.response.data.detail === 'string') {
          errorMsg = error.response.data.detail;
        } else if (Array.isArray(error.response.data.detail)) {
          errorMsg = error.response.data.detail.map(e => e.msg || String(e)).join(', ');
        }
      }
      toast.error(errorMsg);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'scheduled':
        return <Badge className="bg-blue-100 text-blue-700">{t('scheduled')}</Badge>;
      case 'completed':
        return <Badge className="bg-green-100 text-green-700">{t('completed')}</Badge>;
      case 'converted':
        return <Badge className="bg-purple-100 text-purple-700">{t('converted')}</Badge>;
      case 'pending_review':
        return <Badge className="bg-amber-100 text-amber-700">{t('pending')}</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  const getDateLabel = (dateStr) => {
    if (!dateStr) return 'Not scheduled';
    try {
      const date = parseISO(dateStr);
      if (isToday(date)) return 'Today';
      if (isTomorrow(date)) return 'Tomorrow';
      if (isPast(date)) return format(date, 'MMM d') + ' (Past)';
      return format(date, 'EEE, MMM d');
    } catch {
      return dateStr;
    }
  };

  const filteredVisits = visits.filter(visit => {
    const matchesSearch = !searchQuery || 
      visit.client_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      visit.job_address?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      visit.service_type?.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesStatus = filterStatus === 'all' || visit.status === filterStatus;
    
    return matchesSearch && matchesStatus;
  });

  // Group visits by date for better visualization
  const scheduledVisits = filteredVisits.filter(v => v.status === 'scheduled');
  const completedVisits = filteredVisits.filter(v => v.status === 'completed');
  const convertedVisits = filteredVisits.filter(v => v.status === 'converted' || v.status === 'pending_review');

  const primaryColor = organization?.brand_primary_color || '#1e3a8a';
  const accentColor = organization?.brand_accent_color || '#fbbf24';

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="site-visits-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold text-foreground">{t('site_visits_title')}</h1>
          <p className="text-muted-foreground mt-1">{t('schedule_manage')}</p>
        </div>
        
        <Button
          onClick={() => {
            if (!clients || clients.length === 0) {
              toast.warning('No customers yet', {
                description: 'Add a customer first, then schedule a site visit.',
                action: {
                  label: 'Add Customer',
                  onClick: () => navigate('/clients'),
                },
              });
              return;
            }
            setShowNewModal(true);
          }}
          className="h-12 px-6 font-bold rounded-xl shadow-sm"
          style={{ backgroundColor: accentColor, color: '#000' }}
          data-testid="new-site-visit-btn"
        >
          <Plus className="w-5 h-5 mr-2" />
          {t('new_site_visit')}
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
          <Input
            placeholder="Search by customer, address, or service..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 h-12 rounded-xl"
            data-testid="search-input"
          />
        </div>
        
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-[180px] h-12 rounded-xl">
            <SelectValue placeholder={t('filter_by_status')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('all')}</SelectItem>
            <SelectItem value="scheduled">{t('scheduled')}</SelectItem>
            <SelectItem value="completed">{t('completed')}</SelectItem>
            <SelectItem value="converted">{t('converted')}</SelectItem>
            <SelectItem value="pending_review">{t('pending')}</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Stats Bar */}
      <div className="grid grid-cols-3 gap-3">
        <div className="bg-blue-50 rounded-xl p-4 text-center">
          <p className="text-2xl font-bold text-blue-700">{scheduledVisits.length}</p>
          <p className="text-sm text-blue-600 font-medium">{t('scheduled')}</p>
        </div>
        <div className="bg-green-50 rounded-xl p-4 text-center">
          <p className="text-2xl font-bold text-green-700">{completedVisits.length}</p>
          <p className="text-sm text-green-600 font-medium">{t('completed')}</p>
        </div>
        <div className="bg-purple-50 rounded-xl p-4 text-center">
          <p className="text-2xl font-bold text-purple-700">{convertedVisits.length}</p>
          <p className="text-sm text-purple-600 font-medium">{t('converted')}</p>
        </div>
      </div>

      {/* Visit List */}
      {filteredVisits.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-2xl border border-gray-100">
          <ClipboardCheck className="w-16 h-16 text-muted-foreground mx-auto mb-4 opacity-40" />
          <h3 className="text-lg font-semibold text-foreground mb-2">{t('no_site_visits_found')}</h3>
          <p className="text-muted-foreground mb-4">{t('schedule_first_visit')}</p>
          <Button
            onClick={() => {
              if (!clients || clients.length === 0) {
                toast.warning('No customers yet', {
                  description: 'Add a customer first, then schedule a site visit.',
                  action: { label: 'Add Customer', onClick: () => navigate('/clients') },
                });
                return;
              }
              setShowNewModal(true);
            }}
            className="font-semibold"
            style={{ backgroundColor: primaryColor }}
          >
            <Plus className="w-4 h-4 mr-2" />
            {t('schedule_site_visit')}
          </Button>
        </div>
      ) : (
        <div className="space-y-3">
          {filteredVisits.map((visit) => (
            <button
              key={visit.id}
              onClick={() => navigate(`/site-visits/${visit.id}`)}
              className="w-full bg-white rounded-xl border border-gray-100 p-4 text-left hover:shadow-md transition-all group"
              data-testid={`visit-card-${visit.id}`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-semibold text-foreground truncate">
                      {visit.client_name}
                    </span>
                    {getStatusBadge(visit.status)}
                  </div>
                  
                  {visit.service_type && (
                    <p className="text-sm text-muted-foreground mb-2">
                      {visit.service_type}
                    </p>
                  )}
                  
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-muted-foreground">
                    <span className="flex items-center gap-1">
                      <Calendar className="w-4 h-4" />
                      {getDateLabel(visit.scheduled_date)}
                    </span>
                    
                    {visit.scheduled_time && (
                      <span className="flex items-center gap-1">
                        <Clock className="w-4 h-4" />
                        {visit.scheduled_time}
                      </span>
                    )}
                    
                    {visit.assigned_estimator_name && (
                      <span className="flex items-center gap-1">
                        <User className="w-4 h-4" />
                        {visit.assigned_estimator_name}
                      </span>
                    )}
                  </div>
                  
                  {visit.job_address && (
                    <p className="text-sm text-muted-foreground mt-2 flex items-center gap-1">
                      <MapPin className="w-4 h-4 flex-shrink-0" />
                      <span className="truncate">{visit.job_address}</span>
                    </p>
                  )}
                </div>
                
                <div className="flex items-center gap-2">
                  {visit.converted_estimate_id && (
                    <Link
                      to={`/estimates/${visit.converted_estimate_id}`}
                      onClick={(e) => e.stopPropagation()}
                      className="p-2 rounded-lg bg-purple-50 text-purple-600 hover:bg-purple-100"
                    >
                      <FileText className="w-4 h-4" />
                    </Link>
                  )}
                  <ChevronRight className="w-5 h-5 text-muted-foreground group-hover:text-primary transition-colors" />
                </div>
              </div>
            </button>
          ))}
        </div>
      )}

      {/* New Site Visit Modal */}
      <Dialog open={showNewModal} onOpenChange={setShowNewModal}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">{t('schedule_site_visit')}</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Customer Select */}
            <div className="space-y-2">
              <Label>{t('customer_label')} *</Label>
              <Select
                value={newVisitData.client_id}
                onValueChange={(value) => {
                  const client = clients.find(c => c.id === value);
                  setNewVisitData(prev => ({
                    ...prev,
                    client_id: value,
                    job_address: client?.address || prev.job_address
                  }));
                }}
              >
                <SelectTrigger data-testid="client-select">
                  <SelectValue placeholder={t('select_customer')} />
                </SelectTrigger>
                <SelectContent>
                  {clients.map((client) => (
                    <SelectItem key={client.id} value={client.id}>
                      {client.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Service Type */}
            <div className="space-y-2">
              <Label>{t('service_type_label')}</Label>
              <Select
                value={newVisitData.service_type}
                onValueChange={(value) => setNewVisitData(prev => ({ ...prev, service_type: value }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder={t('select_service_type')} />
                </SelectTrigger>
                <SelectContent>
                  {(organization?.service_types_enabled || ['Concrete', 'Landscaping', 'Roofing', 'General']).map((type) => (
                    <SelectItem key={type} value={type}>{type}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Job Address */}
            <div className="space-y-2">
              <Label>{t('job_address_label')}</Label>
              <Input
                value={newVisitData.job_address}
                onChange={(e) => setNewVisitData(prev => ({ ...prev, job_address: e.target.value }))}
                placeholder={t('enter_job_address')}
                data-testid="job-address-input"
              />
            </div>

            {/* Date & Time */}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>{t('date_label')}</Label>
                <Input
                  type="date"
                  value={newVisitData.scheduled_date}
                  onChange={(e) => setNewVisitData(prev => ({ ...prev, scheduled_date: e.target.value }))}
                  data-testid="date-input"
                />
              </div>
              <div className="space-y-2">
                <Label>{t('time_label')}</Label>
                <Input
                  type="time"
                  value={newVisitData.scheduled_time}
                  onChange={(e) => setNewVisitData(prev => ({ ...prev, scheduled_time: e.target.value }))}
                  data-testid="time-input"
                />
              </div>
            </div>

            {/* Assigned Estimator */}
            <div className="space-y-2">
              <Label>{t('assigned_to')}</Label>
              <Select
                value={newVisitData.assigned_estimator_id || "unassigned"}
                onValueChange={(value) => setNewVisitData(prev => ({ ...prev, assigned_estimator_id: value === "unassigned" ? "" : value }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder={t('assigned_to')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="unassigned">-</SelectItem>
                  {employees.filter(e => e.is_active).map((emp) => (
                    <SelectItem key={emp.id} value={emp.id}>
                      {emp.first_name} {emp.last_name} ({emp.role_type})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Notes */}
            <div className="space-y-2">
              <Label>{t('notes_label')}</Label>
              <Textarea
                value={newVisitData.project_notes}
                onChange={(e) => setNewVisitData(prev => ({ ...prev, project_notes: e.target.value }))}
                rows={3}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewModal(false)}>
              {t('cancel')}
            </Button>
            <Button
              onClick={handleCreateVisit}
              style={{ backgroundColor: primaryColor }}
              data-testid="create-visit-btn"
            >
              Schedule Visit
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SiteVisitsPage;
